import { useCallback } from 'react';

import useAiReview from '~hooks/useAiReview/useAiReview';
import { trackAiReviewRejected } from '~hooks/useAnalytics/trackApprovals';
import { Load } from '~hooks/useLoad/models';

interface UseAiReviewDismissalArgs {
  companyId: string;
  load: Load;
  onChanged: () => void;
}

export default function useAiReviewDismissal({
  companyId,
  load,
  onChanged,
}: UseAiReviewDismissalArgs) {
  const { dismissAiReview, undismissAiReview } = useAiReview();
  const aiReviewId = load.aiReview?.id;

  // The endpoints answer with the review, not its load, so the row's new state has to come
  // from the server. A superseded review takes the same path: either way what the user was
  // looking at is no longer what the load holds.
  const dismiss = useCallback(async () => {
    if (!aiReviewId) {
      return;
    }

    const dismissedReview = await dismissAiReview({ aiReviewId, companyId });

    if (dismissedReview) {
      trackAiReviewRejected(load.id);
    }

    onChanged();
  }, [aiReviewId, companyId, dismissAiReview, load.id, onChanged]);

  const undismiss = useCallback(async () => {
    if (!aiReviewId) {
      return;
    }

    await undismissAiReview({ aiReviewId, companyId });

    onChanged();
  }, [aiReviewId, companyId, onChanged, undismissAiReview]);

  return { dismiss, undismiss };
}
